import { CommonModule } from "@angular/common";
import { Component, Input, OnInit, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { Observable, filter, take } from "rxjs";
import { ZorroTableComponent } from "../../../../components/common/custom-table/zorro-table.component";
import { GenericModalComponent } from "../../../../components/modal-templates/user-story-basic-view/generic-modal.component";
import { SharedComponents } from "../../../../components/shared.module";
import {
  dummyModalFields,
  testSourceCodeMappingData,
} from "../../../../core/dummy/dummy-data";
import { testPrioritizationModalConfig } from "../../../../core/generic-templates/modal-config-templates";
import { INZorroOutput } from "../../../../core/models/common";
import { TestPrioritizationActions } from "../../../../core/ngrx/actions/test-prioritization.action";
import {
  selectLoading,
  selectMappingResponse,
  selectTestCasesToSourceMapping,
} from "../../../../core/ngrx/selectors/test-prioritization.selectors";
import { RestService } from "../../../../core/services/rest.service";
import { ToasterService } from "../../../../core/services/toaster-service";
import { UtilitiesService } from "../../../../core/services/utilities.service";
import { testSourceCodeMappingColumns } from "../../../../core/table-templates/test-source-code-mapping-table";
import { MessageEnum } from "../../../../core/utils/message.enum";

/**
 * Component for managing and approving test case to source code mappings.
 * Provides functionalities such as filtering, approving, and handling table actions.
 * Utilizes Angular Material, NgRx for state management, and PrimeNG for messaging.
 */
@Component({
  selector: "app-mapping-approval",
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    ZorroTableComponent,
    FormsModule,
    ...SharedComponents,
  ],
  templateUrl: "./mapping-approval.component.html",
  styleUrl: "./mapping-approval.component.scss",
})
export class MappingApprovalComponent implements OnInit {
  @Input()
  clusterId: any = "TestCase to Source Code Mappings";
  filteredRows: any[] = [];
  searchValue: string = "";
  contentItems: any = dummyModalFields;
  isLoading: boolean = false;

  private modalService = inject(NgbModal);
  sourceCodeMappingTestCases$!: Observable<any[]>;
  mappingResponse$ = this.store.select(selectMappingResponse);
  mappingId: any = "";
  selectedTestCases: any[] = [];
  tableData: any;
  loading$!: Observable<boolean>;

  /**
   * Constructor to initialize services and load initial store data.
   * @param store NGRX store for state management.
   * @param restService REST service for API calls.
   */
  constructor(
    private store: Store,
    private restService: RestService,
    private utilitiService: UtilitiesService,
    private readonly toaster: ToasterService
  ) {

  }

  /**
   * Angular lifecycle hook triggered on component initialization.
   * Subscribes to mapping responses and loads test cases for mapping.
   */
  ngOnInit() {
    this.sourceCodeMappingTestCases$ = this.store.select(
      selectTestCasesToSourceMapping
    );
    this.loading$ = this.store.select(selectLoading);
    this.mappingResponse$
      .pipe(
        filter((response) => !!response),
        take(1)
      )
      .subscribe((response) => {
        console.log("Response received in component:", response);
        this.mappingId = response.id;
       // this.loadSourceCodeMappingTestCases();
      });

    this.tableData = {
      columns: testSourceCodeMappingColumns,
      rows: testSourceCodeMappingData,
    };

    this.filteredRows = this.tableData.rows;

    this.loadStoreData();
  }

  /**
   * Dispatches action to load test cases associated with the current mapping ID.
   */
  loadSourceCodeMappingTestCases() {
    this.utilitiService.dispatchIfEmpty(
      this.sourceCodeMappingTestCases$,
      TestPrioritizationActions.loadTestCases({
        mappingId: this.mappingId,
      })
    );
  }

  /**
   * Loads and processes store data for displaying in the table.
   * Adds a `select` property to each row for selection purposes.
   */
  loadStoreData() {
    // this.filteredRows = s
    this.store.select(selectTestCasesToSourceMapping).subscribe(
      (items) => {
        const updatedItems = items.map((item) => ({
          ...item,
          select: false,
        }));
        this.tableData.rows = updatedItems;
        this.filteredRows = updatedItems;
      //  this.isLoading = false;
      },
      (error) => {
        console.error("Error occurred:", error);
      //  this.isLoading = false;
        this.tableData.rows = [];
        this.filteredRows = [];
      }
    );

    this.loading$.subscribe(item => {
      this.isLoading = item;
    })
  }

  /**
   * Filters the table data based on the search input.
   * Matches values across all columns for a given search term.
   */
  filterTableData() {
    const searchValueLower = this.searchValue.toLowerCase();
    // this.filteredRows = this.data.tableData.rows.filter((row: any) =>
    //   row.testCaseTitle.toLowerCase().includes(searchValueLower)
    // );
    this.filteredRows = this.tableData?.rows?.filter((row: any) =>
      Object.values(row).some((value: any) =>
        value.toString().toLowerCase().includes(searchValueLower)
      )
    );
  }

  /**
   * Approves the selected mappings and sends the data to the server.
   * Displays success or error messages based on the API response.
   */
  approveMappings() {
    const payload = {
      id: this.mappingId,
      data: this.selectedTestCases.map(({ select, ...rest }) => rest),
    };

    this.restService.approveMapping(payload).subscribe({
      next: () => {
        this.toaster.pop({ type: 'success', text: MessageEnum.TESTCASE_CODE_MAPPING_SUCCESS });
      },
      error: (error) => {
        this.toaster.pop({ type: 'error', text: MessageEnum.TESTCASE_CODE_MAPPING_FAILURE });
        console.error("Approve Mapping Error:", error);
      },
    });
  }

  /**
   * Handles custom actions triggered from the table.
   * Opens a modal when the "open_modal" action is triggered.
   * @param event Event containing table action details.
   */
  handleTableActions(event: INZorroOutput) {
    if (event.data.column?.key === "open_modal") {
      this.openModal(event.data.row);
    }
  }

  /**
   * Updates the selected test cases when a row is selected in the table.
   * @param event Event containing the selected rows.
   */
  handleRowSelect(event: any) {
    this.selectedTestCases = event?.data;
  }

  /**
   * Opens a modal with details of the selected row.
   * @param data Data to display in the modal.
   */
  openModal(data: any) {
    const modalRef = this.modalService.open(GenericModalComponent, {
      centered: true,
      windowClass: "user-story-modal",
      size: "xl",
      backdrop: 'static',
    });
    modalRef.componentInstance.data = data;
    modalRef.componentInstance.contentList =
      testPrioritizationModalConfig.testCaseSourceCodeMapping;
    modalRef.componentInstance.title =
      "Test Case to Source Code Mapping - Details";
  }
}
